/**
 * Title and body for message notifications. Toasts, OS notifications, and
 * inbox rows all render through these so they always show the same details,
 * filtered by the alias notification detail settings.
 */

import type {
  ConversationNotificationDisplay,
  SpaceNotificationDisplay,
} from '@adieuu/shared';

export type NotificationTranslate = (key: string, options?: Record<string, unknown>) => string;

export interface NotificationCopy {
  title: string;
  body: string;
}

export type ConversationNotificationKind = 'message' | 'reply' | 'mention' | 'reaction';

export type SpaceNotificationKind =
  | 'message'
  | 'reply'
  | 'mention'
  | 'mentionHere'
  | 'mentionRole'
  | 'reaction';

const DEFAULTS = {
  someone: 'Someone',
  yourDm: 'your DM',
  yourGroup: 'your group',
  aChannel: 'a channel',
  channelRef: '#{{channel}}',
  titles: {
    message: 'New message',
    reply: 'New reply',
    mention: 'Mention',
    reaction: 'Reaction',
  },
  conversation: {
    message: '{{sender}} responded in {{where}}',
    reply: '{{sender}} replied to you in {{where}}',
    mention: '{{sender}} mentioned you in {{where}}',
    reaction: '{{sender}} reacted to your message in {{where}}',
  },
  space: {
    message: '{{sender}} posted in {{where}}',
    reply: '{{sender}} replied to you in {{where}}',
    mention: '{{sender}} mentioned you in {{where}}',
    mentionHere: '{{sender}} mentioned @here in {{where}}',
    mentionRole: '{{sender}} mentioned a role you have in {{where}}',
    reaction: '{{sender}} reacted to your message in {{where}}',
    reactionEmoji: '{{sender}} reacted {{emoji}} to your message in {{where}}',
  },
} as const;

function interpolate(template: string, params: Record<string, unknown>): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => {
    const value = params[name];
    return value === undefined || value === null ? '' : String(value);
  });
}

/** Renders the English default copy; for callers without an i18n `t`. */
export const fallbackNotificationTranslate: NotificationTranslate = (_key, options) =>
  interpolate(String(options?.defaultValue ?? ''), options ?? {});

function copy(
  t: NotificationTranslate,
  key: string,
  defaultValue: string,
  params: Record<string, unknown> = {},
): string {
  return t(`notifications.copy.${key}`, { ...params, defaultValue });
}

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function senderLabel(
  t: NotificationTranslate,
  showSender: boolean,
  senderName: string | null | undefined,
): string {
  const name = showSender ? nonEmpty(senderName) : undefined;
  return name ?? copy(t, 'someone', DEFAULTS.someone);
}

function kindTitle(
  t: NotificationTranslate,
  kind: ConversationNotificationKind | SpaceNotificationKind,
): string {
  const titleKind = kind === 'mentionHere' || kind === 'mentionRole' ? 'mention' : kind;
  return copy(t, `titles.${titleKind}`, DEFAULTS.titles[titleKind]);
}

export interface ConversationNotificationInput {
  kind: ConversationNotificationKind;
  senderName?: string | null;
  conversation: {
    type?: string | null;
    /** Decrypted group name or DM topic, when set. */
    name?: string | null;
  };
  display: ConversationNotificationDisplay;
  t: NotificationTranslate;
}

export function describeConversationNotification(input: ConversationNotificationInput): NotificationCopy {
  const { kind, senderName, conversation, display, t } = input;
  const sender = senderLabel(t, display.showSender, senderName);
  const name = display.showConversationName ? nonEmpty(conversation.name) : undefined;
  const where =
    name ??
    (conversation.type === 'group'
      ? copy(t, 'yourGroup', DEFAULTS.yourGroup)
      : copy(t, 'yourDm', DEFAULTS.yourDm));
  return {
    title: kindTitle(t, kind),
    body: copy(t, `conversation.${kind}`, DEFAULTS.conversation[kind], { sender, where }),
  };
}

export interface SpaceNotificationInput {
  kind: SpaceNotificationKind;
  senderName?: string | null;
  channelName?: string | null;
  spaceName?: string | null;
  /** Reaction glyph (reactions only); custom emoji without a glyph omit it. */
  emoji?: string | null;
  display: SpaceNotificationDisplay;
  t: NotificationTranslate;
}

export function describeSpaceNotification(input: SpaceNotificationInput): NotificationCopy {
  const { kind, senderName, channelName, spaceName, emoji, display, t } = input;
  const sender = senderLabel(t, display.showSender, senderName);
  const channel = display.showChannel ? nonEmpty(channelName) : undefined;
  const where = channel
    ? copy(t, 'channelRef', DEFAULTS.channelRef, { channel })
    : copy(t, 'aChannel', DEFAULTS.aChannel);
  const space = display.showSpace ? nonEmpty(spaceName) : undefined;
  const glyph = kind === 'reaction' ? nonEmpty(emoji) : undefined;
  const body = glyph
    ? copy(t, 'space.reactionEmoji', DEFAULTS.space.reactionEmoji, { sender, where, emoji: glyph })
    : copy(t, `space.${kind}`, DEFAULTS.space[kind], { sender, where });
  return { title: space ?? kindTitle(t, kind), body };
}
